import React from 'react';
import { motion } from 'framer-motion';
import { useCategories } from '../../../hooks/useCategories';
import { TrendingUp, AlertCircle } from 'lucide-react';

/* =======================
   UI Category Shape
   ======================= */
interface UICategory {
  id: string;
  name: string;
  description: string;
  expertCount: number;
  icon?: string | null;
  gradient?: string | null;
  iconColor?: string | null;
}

interface CategoryGridProps {
  searchQuery: string;
  filters?: any;
  onSelectCategory: (categoryId: string) => void;
}

  //this bullshit below for extracting color from backend api 
  
function extractGradient(bg: string) {
  // "from-[#F4A11A]/20 to-[#F4A11A]/10"
  const fromMatch = bg.match(/from-\[#([0-9A-Fa-f]{6})\]\/(\d+)/);
  const toMatch = bg.match(/to-\[#([0-9A-Fa-f]{6})\]\/(\d+)/);

  if (!fromMatch || !toMatch) return '';

  const fromColor = `rgba(${hexToRgb(fromMatch[1])}, ${Number(fromMatch[2]) / 100})`;
  const toColor = `rgba(${hexToRgb(toMatch[1])}, ${Number(toMatch[2]) / 100})`;

  return `${fromColor}, ${toColor}`;
}

function hexToRgb(hex: string) {
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
}

/* =======================
   SKELETON CARD
   ======================= */
function SkeletonCard({ index }: { index: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="relative rounded-2xl p-6 h-full border-2 border-red-200 bg-gradient-to-br from-red-50 to-red-100 w-full sm:w-[280px]"
    >
      {/* Error Icon */}
      <div className="mb-4">
        <div className="w-14 h-14 rounded-xl bg-red-100 shadow-sm flex items-center justify-center">
          <AlertCircle className="w-7 h-7 text-red-400" />
        </div>
      </div>

      {/* Content */}
      <h3 className="text-gray-900 mb-2 font-semibold">Unable to Load</h3>
      <p className="text-sm text-gray-600 mb-4">
        We're having trouble loading popular categories right now.
      </p>

      {/* Footer message */}
      <div className="text-xs text-gray-500 mb-2">
        <span className="font-medium">🧙 Mr. Developer is fighting a nasty bug with his stick!</span>
      </div>
      <div className="text-xs text-gray-400">
        Stay tuned, Coming soon.
      </div>
    </motion.div>
  );
}

export function CategoryGrid({ searchQuery, onSelectCategory }: CategoryGridProps) {
  const { categories, loading, error } = useCategories();

  /* =======================
     BACKEND → UI MAP
     ======================= */
  const uiCategories: UICategory[] = categories.map((cat) => ({
    id: String(cat.id),
    name: cat.name,
    description: cat.description || '',
    expertCount: 100, // unchanged UI behavior
    icon: cat.icon ?? null,
    gradient: cat.bg ?? '',
    iconColor: cat.iconColor ?? '#2563eb',
  }));

  const filteredCategories = uiCategories.filter(
    (category) =>
      category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      category.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Show skeleton cards if error or no categories
  const showSkeletons = error || (!loading && categories.length === 0);

  if (loading) return null;

  return (
    <div className="mt-12 font-['Poppins'] w-full max-w-7xl mx-auto px-4">
      <h2 className="flex items-center gap-2 text-lg sm:text-xl font-normal text-gray-900 mb-6 sm:mb-8">
        <span className="text-base sm:text-xl">All Categories</span>
        <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
      </h2>
      
      {/* 🟢 FIXED: Converted container to wrap flexibly and self-center perfectly on all viewport resolutions */}
      <div className="flex flex-wrap justify-center gap-6 w-full mx-auto">
        {showSkeletons ? (
          // Show 3 skeleton cards when backend fails
          Array.from({ length: 3 }).map((_, index) => (
            <SkeletonCard key={index} index={index} />
          ))
        ) : (
          // Show actual categories
          filteredCategories.map((category, index) => (
            <CategoryCard
              key={category.id}
              category={category}
              index={index}
              onSelect={onSelectCategory}
            />
          ))
        )}
      </div>
    </div>
  );
}

/* =======================
   CARD (UI UNCHANGED)
   ======================= */
function CategoryCard({
  category,
  index,
  onSelect,
}: {
  category: UICategory;
  index: number;
  onSelect: (id: string) => void;
}) {
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group cursor-pointer w-full sm:w-[280px]"
      onClick={() => onSelect(category.id)}
    >
      <motion.div
        whileHover={{ y: -8 }}
        transition={{ type: 'spring', stiffness: 300 }}
        style={{
          background: category.gradient
            ? `linear-gradient(135deg, ${extractGradient(category.gradient)})`
            : undefined,
        }}
        className={`relative rounded-2xl p-6 h-full border-2 border-transparent transition-all duration-300 ${
          isHovered ? 'shadow-xl border-blue-400' : 'shadow-sm'
        }`}
      >
        {/* Icon */}
        <div className="mb-4">
          <div
            className={`w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center transition-transform duration-300 ${
              isHovered ? 'scale-110' : ''
            }`}
          >
            {category.icon ? (
              <img
                src={category.icon}
                alt={category.name}
                className="w-7 h-7 object-contain"
              />
            ) : null}
          </div>
        </div>

        {/* Content */}
        <h3 className="text-gray-900 mb-2">{category.name}</h3>
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {category.description}
        </p>

        {/* Expert Count */}
        <div className="text-sm text-gray-700 mb-3">
          <span className="font-medium">{category.expertCount}+</span> experts
        </div>

        {/* CTA */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 10 }}
          transition={{ duration: 0.2 }}
          className="w-full px-4 py-2 bg-brand-blue-900 text-white rounded-lg hover:bg-brand-blue-800 transition-colors text-sm"
        >
          Browse this category
        </motion.button>
      </motion.div>
    </motion.div>
  );
}